import { parseJson } from "@arti/utilities";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { LoadingSpinner } from "../common/LoadingState";
import {
  type ChannelEnvironment,
  type PreviewPublicConfigReason,
  previewPublicConfigReasonDetail,
} from "./useChannelEnvironment";

/**
 * Live preview, embedded as a nested drawer that slides out left-adjacent to
 * the instance drawer (which is pinned right at max-w-3xl). The iframe loads
 * the preview funnel host's one-time auth URL with `embed=1`; the proxy sets a
 * partitioned session cookie inside the frame and postMessages back
 * `arti.preview.embed.ready` once routing is live. Browsers that refuse the
 * partitioned third-party cookie (Safari ITP / Firefox TCP) never send the
 * ready signal — a short timeout flips us to the open-in-tab fallback so the
 * drawer never hangs on a blank frame.
 *
 * Stacking: the instance SideSheet renders a z-50 backdrop, so this sits at
 * z-[60] above it. A transparent max-w-3xl spacer reserves the instance
 * drawer's footprint (so it shows through and stays interactive); the left
 * click-catcher dismisses the preview without closing the instance drawer.
 */

const EMBED_READY_TYPE = "arti.preview.embed.ready";
const EMBED_FAILED_TYPE = "arti.preview.embed.failed";
/** How long to wait for the in-frame ready signal before surfacing a neutral
 *  fallback instead of leaving the drawer on a blank frame. */
const EMBED_READY_TIMEOUT_MS = 7000;
const TAB_LAUNCH_TIMEOUT_MS = 15_000;
/** Storage key announcing the most recent preview auth across dashboard
 *  tabs. The funnel host holds exactly ONE session cookie per browser, so an
 *  auth in any tab silently re-routes every other open preview iframe (the
 *  swap becomes visible on the next in-frame load — hot-reload reloads
 *  included). Tabs watch this marker and reclaim the session when the user
 *  returns to them. */
const PREVIEW_AUTH_MARKER_KEY = "arti.preview.lastAuth";

// The preview is a different origin (the preview funnel host), so
// allow-scripts + allow-same-origin cannot escape into the dashboard origin —
// it only restores the frame's own origin so its app, cookies and WS work.
// allow-top-navigation is intentionally withheld so the embedded dev app can't
// hijack the dashboard tab.
const PREVIEW_SANDBOX =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-pointer-lock";

type EmbedState = "minting" | "embedding" | "live" | "blocked" | "error";
type EmbedFailureReason =
  | "cookie_unavailable"
  | "owner_unreachable"
  | "timeout";
type Viewport = "desktop" | "mobile";
interface PreviewOverlayAction {
  disabled: boolean;
  label: string;
  onClick: () => void;
}
// Phone-class viewport width; the app inside the iframe renders its mobile
// breakpoint while the drawer chrome stays put.
const MOBILE_VIEWPORT_WIDTH = 390;

interface PreviewSurfaceProps {
  onClose: () => void;
  environment: ChannelEnvironment | null;
  launchPreview: (publicPort: number) => Promise<string>;
  /** Instance/env/room label for the header. */
  title: string;
  /** Esc-to-close is right for the OVERLAY host (drawer) but hostile for an
   *  in-page split pane where focus usually sits in the chat composer.
   *  Defaults to true (drawer behavior). */
  closeOnEscape?: boolean;
}

/**
 * Chrome-free preview: header (port, status, viewport toggle, tab, close) +
 * iframe body with the full mint/handshake/takeover state machine. Mounting
 * the component IS opening the preview — hosts (the actor-drawer overlay, the
 * room hub's split pane) unmount it to close. Extracted from PreviewDrawer so
 * both hosts share one session lifecycle implementation.
 */
export const PreviewSurface: FC<PreviewSurfaceProps> = ({
  onClose,
  environment,
  launchPreview,
  title,
  closeOnEscape = true,
}) => {
  const ports = useMemo(() => environment?.previewPorts ?? [], [environment]);
  const running = environment?.status === "running";
  const previewPublicConfigured = environment?.previewPublicConfigured ?? false;
  const previewPublicConfigReason =
    environment?.previewPublicConfigReason ?? null;
  // The env hook is stale-while-revalidate: switching the focused actor swaps
  // the environment object under an open drawer without changing port/open
  // state (envs commonly share publicPort 61000), so the mint effect must key
  // on the env's identity or the iframe keeps showing the previous env.
  const environmentId = environment?.channelEnvironmentId ?? null;
  const [selectedPort, setSelectedPort] = useState<number | null>(
    ports[0]?.publicPort ?? null,
  );
  const [state, setState] = useState<EmbedState>("minting");
  const [embedFailureReason, setEmbedFailureReason] =
    useState<EmbedFailureReason | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tabOpening, setTabOpening] = useState(false);
  // Bumped to force a fresh mint (Retry).
  const [reloadNonce, setReloadNonce] = useState(0);
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const previewOriginRef = useRef<string | null>(null);
  const timeoutRef = useRef<number | null>(null);
  // The env hook polls and returns a fresh launchPreview closure each tick;
  // pin it so the mint effect doesn't re-fire (and reload the iframe) every
  // poll interval.
  const launchPreviewRef = useRef(launchPreview);
  useEffect(() => {
    launchPreviewRef.current = launchPreview;
  }, [launchPreview]);

  // Keep a valid selected port as the env's ports change.
  useEffect(() => {
    if (
      ports.length > 0 &&
      !ports.some((port) => port.publicPort === selectedPort)
    ) {
      setSelectedPort(ports[0].publicPort);
    }
  }, [ports, selectedPort]);

  const clearReadyTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Mint a fresh embed URL whenever the surface mounts, the target env
  // changes (actor/room switch), or the target port changes.
  useEffect(() => {
    if (
      selectedPort === null ||
      !running ||
      environmentId === null ||
      !previewPublicConfigured
    ) {
      setSrc(null);
      clearReadyTimeout();
      return;
    }
    let cancelled = false;
    setState("minting");
    setEmbedFailureReason(null);
    setSrc(null);
    setErrorMessage(null);
    void (async () => {
      try {
        const url = await launchPreviewRef.current(selectedPort);
        if (cancelled) {
          return;
        }
        previewOriginRef.current = new URL(url).origin;
        const embedUrl = `${url}${url.includes("?") ? "&" : "?"}embed=1`;
        try {
          window.localStorage.setItem(
            PREVIEW_AUTH_MARKER_KEY,
            JSON.stringify({ environmentId, ts: Date.now() }),
          );
        } catch {
          // Storage unavailable (private mode quirks) — takeover detection
          // degrades gracefully; the drawer still re-mints on open.
        }
        setSrc(embedUrl);
        setState("embedding");
        clearReadyTimeout();
        timeoutRef.current = window.setTimeout(() => {
          setEmbedFailureReason("timeout");
          setState((prev) => (prev === "embedding" ? "blocked" : prev));
        }, EMBED_READY_TIMEOUT_MS);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setErrorMessage(error instanceof Error ? error.message : String(error));
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
      clearReadyTimeout();
    };
  }, [
    selectedPort,
    running,
    environmentId,
    previewPublicConfigured,
    clearReadyTimeout,
    reloadNonce,
  ]);

  // Cross-tab session takeover: when another tab auths a DIFFERENT env, this
  // drawer's funnel cookie is gone and its iframe will swap apps on its next
  // internal load. Mark the session stale on the storage event, then reclaim
  // (fresh mint → fresh cookie → iframe reload) when the user returns to this
  // tab — reclaim-on-return rather than instantly, so two visible previews
  // don't ping-pong auths; the one the user focuses wins deterministically.
  const sessionStaleRef = useRef(false);
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== PREVIEW_AUTH_MARKER_KEY || event.newValue === null) {
        return;
      }
      const marker = parseJson(event.newValue);
      if (
        !isPreviewAuthMarker(marker) ||
        marker.environmentId !== environmentId
      ) {
        sessionStaleRef.current = true;
      }
    };
    const reclaim = () => {
      if (sessionStaleRef.current && document.visibilityState === "visible") {
        sessionStaleRef.current = false;
        setReloadNonce((n) => n + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", reclaim);
    document.addEventListener("visibilitychange", reclaim);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", reclaim);
      document.removeEventListener("visibilitychange", reclaim);
    };
  }, [environmentId]);

  // Listen for the in-frame ready/failed handshake from the preview origin only.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (
        previewOriginRef.current === null ||
        event.origin !== previewOriginRef.current
      ) {
        return;
      }
      const { data } = event;
      if (!isPreviewMessage(data)) {
        return;
      }
      if (data?.type === EMBED_READY_TYPE) {
        clearReadyTimeout();
        setEmbedFailureReason(null);
        setState("live");
      } else if (data?.type === EMBED_FAILED_TYPE) {
        clearReadyTimeout();
        setEmbedFailureReason(previewFailureReason(data));
        setState("blocked");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [clearReadyTimeout]);

  // Esc closes the preview (hosts keep their own Esc handling for when the
  // preview is shut).
  useEffect(() => {
    if (!closeOnEscape) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, closeOnEscape]);

  const openInTab = useCallback(async () => {
    if (selectedPort === null) {
      return;
    }
    if (!previewPublicConfigured) {
      setErrorMessage(
        previewPublicConfigReason
          ? previewPublicConfigReasonDetail(previewPublicConfigReason)
          : "Preview public URL config is not launchable.",
      );
      setState("error");
      return;
    }
    setTabOpening(true);
    try {
      const url = await withTimeout(
        launchPreview(selectedPort),
        TAB_LAUNCH_TIMEOUT_MS,
        "Timed out opening preview. The preview service did not respond.",
      );
      // The tab auth claims the funnel cookie exactly like an embed mint —
      // announce it so drawers showing other envs know they went stale.
      try {
        window.localStorage.setItem(
          PREVIEW_AUTH_MARKER_KEY,
          JSON.stringify({ environmentId, ts: Date.now() }),
        );
      } catch {
        // Storage unavailable — takeover detection degrades gracefully.
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : String(error));
      setState("error");
    } finally {
      setTabOpening(false);
    }
  }, [
    launchPreview,
    selectedPort,
    previewPublicConfigured,
    previewPublicConfigReason,
    environmentId,
  ]);

  const activePort = ports.find((port) => port.publicPort === selectedPort);
  const portLabel =
    activePort?.label ?? `Port ${activePort?.containerPort ?? "—"}`;
  const retryPreview = useCallback(() => {
    setErrorMessage(null);
    setEmbedFailureReason(null);
    setState("minting");
    setReloadNonce((n) => n + 1);
  }, []);
  const blockedOverlay = blockedOverlayContent({
    embedFailureReason,
    openInTab,
    retryPreview,
    tabOpening,
  });

  const statusDot = ((): { className: string; label: string } => {
    if (!running) {
      return { className: "bg-amber-400", label: "waiting" };
    }
    if (!previewPublicConfigured) {
      return { className: "bg-rose-400", label: "unconfigured" };
    }
    switch (state) {
      case "live": {
        return { className: "bg-emerald-400 animate-pulse", label: "live" };
      }
      case "blocked": {
        return { className: "bg-slate-400", label: "tab only" };
      }
      case "error": {
        return { className: "bg-rose-400", label: "error" };
      }
      default: {
        return { className: "bg-amber-400 animate-pulse", label: "connecting" };
      }
    }
  })();

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Header — mirrors the SideSheet mono/bracket idiom. */}
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-slate-300/70 bg-white px-3 dark:border-slate-700 dark:bg-arti-background py-2 sm:px-4 sm:py-2.5">
        <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
          [ Preview ]
        </span>
        <span
          className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${statusDot.className}`}
          title={statusDot.label}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] uppercase tracking-wider text-slate-600 dark:text-slate-400">
          {portLabel}
          <span className="ml-1 text-slate-600">· {statusDot.label}</span>
        </span>

        {ports.length > 1 && (
          <select
            value={selectedPort ?? ""}
            onChange={(e) => setSelectedPort(Number(e.target.value))}
            className="border border-slate-300/70 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 focus:border-emerald-500/60 focus:outline-none"
            title="Preview port"
          >
            {ports.map((port) => (
              <option key={port.publicPort} value={port.publicPort}>
                {port.label ?? `:${port.containerPort}`}
              </option>
            ))}
          </select>
        )}

        <ViewportToggle value={viewport} onChange={setViewport} />

        <button
          type="button"
          onClick={() => void openInTab()}
          disabled={!running || !previewPublicConfigured || tabOpening}
          title={getOpenInTabTitle({
            previewPublicConfigReason,
            previewPublicConfigured,
          })}
          className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-700 hover:text-emerald-900 disabled:cursor-not-allowed disabled:text-slate-400 dark:text-emerald-400 dark:hover:text-emerald-200 dark:disabled:text-slate-600"
        >
          {tabOpening ? "opening…" : "↗ tab"}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          className="font-mono text-sm leading-none text-slate-500 hover:text-slate-900 dark:hover:text-slate-200"
        >
          ✕
        </button>
      </div>

      {/* Body — the iframe fills the drawer in desktop, or sits in a
            centered phone-width column (letterboxed) in mobile. */}
      <div className="relative flex min-h-0 flex-1 justify-center bg-slate-100 dark:bg-slate-950">
        {src && running && previewPublicConfigured && (
          <div
            className={`relative h-full ${
              viewport === "mobile"
                ? "w-full max-w-[390px] border-x border-slate-700/70 shadow-2xl shadow-black/40"
                : "w-full"
            }`}
            style={
              viewport === "mobile"
                ? { maxWidth: MOBILE_VIEWPORT_WIDTH }
                : undefined
            }
          >
            <iframe
              key={src}
              title={`${title} live preview`}
              src={src}
              className="h-full w-full border-0 bg-white"
              sandbox={PREVIEW_SANDBOX}
              allow="fullscreen"
              referrerPolicy="no-referrer"
            />
          </div>
        )}

        {!running && (
          <PreviewOverlay
            tone="waiting"
            title="Workspace not running"
            detail="Previews go live once the workspace is running."
          />
        )}

        {running && !previewPublicConfigured && (
          <PreviewOverlay
            tone="error"
            title="Preview unconfigured"
            detail={
              previewPublicConfigReason
                ? previewPublicConfigReasonDetail(previewPublicConfigReason)
                : "Preview public URL config is not launchable."
            }
          />
        )}

        {running &&
          previewPublicConfigured &&
          (state === "minting" || state === "embedding") && (
            <PreviewOverlay
              tone="loading"
              title="Establishing preview session…"
              detail="Authenticating the embedded frame."
            />
          )}

        {running && previewPublicConfigured && state === "blocked" && (
          <PreviewOverlay
            tone="blocked"
            title={blockedOverlay.title}
            detail={blockedOverlay.detail}
            action={blockedOverlay.action}
            secondaryAction={blockedOverlay.secondaryAction}
          />
        )}

        {running && previewPublicConfigured && state === "error" && (
          <PreviewOverlay
            tone="error"
            title="Preview unavailable"
            detail={errorMessage ?? "The preview could not be reached."}
            action={{ disabled: false, label: "Retry", onClick: retryPreview }}
          />
        )}
      </div>
    </div>
  );
};

function blockedOverlayContent(input: {
  embedFailureReason: EmbedFailureReason | null;
  openInTab: () => Promise<void>;
  retryPreview: () => void;
  tabOpening: boolean;
}): {
  action: PreviewOverlayAction;
  detail: string;
  secondaryAction?: PreviewOverlayAction;
  title: string;
} {
  const openInTabAction = {
    disabled: input.tabOpening,
    label: input.tabOpening ? "opening…" : "↗ Open in new tab",
    onClick: () => void input.openInTab(),
  };
  const retryAction = {
    disabled: false,
    label: "Retry",
    onClick: input.retryPreview,
  };
  switch (input.embedFailureReason) {
    case "owner_unreachable": {
      return {
        action: retryAction,
        detail:
          "The preview host can't be reached right now — this is a service issue, not your browser.",
        title: "Preview host unreachable",
      };
    }
    case "timeout": {
      return {
        action: openInTabAction,
        detail:
          "Preview didn't respond in time. Open it in a tab or retry the embedded preview.",
        secondaryAction: retryAction,
        title: "Preview didn't respond",
      };
    }
    case "cookie_unavailable":
    case null: {
      return {
        action: openInTabAction,
        detail:
          "Safari and Firefox restrict partitioned third-party cookies, so the preview can't authenticate inside the dashboard. Open it in a tab instead.",
        title: "This browser blocks embedded previews",
      };
    }
  }
}

async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  message: string,
): Promise<T> {
  let timeout: number | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeout = window.setTimeout(
          () => reject(new Error(message)),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timeout !== null) {
      window.clearTimeout(timeout);
    }
  }
}

function getOpenInTabTitle(input: {
  previewPublicConfigReason: PreviewPublicConfigReason | null;
  previewPublicConfigured: boolean;
}): string {
  if (input.previewPublicConfigured) {
    return "Open this preview in a new tab";
  }
  if (input.previewPublicConfigReason) {
    return previewPublicConfigReasonDetail(input.previewPublicConfigReason);
  }
  return "Preview public URL config is not launchable";
}

function isPreviewAuthMarker(
  value: unknown,
): value is { environmentId?: unknown } {
  return isRecord(value);
}

function isPreviewMessage(
  value: unknown,
): value is { reason?: unknown; type?: unknown } {
  return isRecord(value);
}

function previewFailureReason(value: { reason?: unknown }): EmbedFailureReason {
  switch (value.reason) {
    case "cookie_unavailable":
    case "owner_unreachable": {
      return value.reason;
    }
    default: {
      return "timeout";
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function PreviewOverlay({
  tone,
  title,
  detail,
  action,
  secondaryAction,
}: {
  tone: "loading" | "waiting" | "blocked" | "error";
  title: string;
  detail: string;
  action?: PreviewOverlayAction;
  secondaryAction?: PreviewOverlayAction;
}) {
  const accentByTone: Record<typeof tone, string> = {
    blocked: "text-slate-700 dark:text-slate-200",
    error: "text-rose-700 dark:text-rose-300",
    loading: "text-emerald-700 dark:text-emerald-300",
    waiting: "text-amber-700 dark:text-amber-300",
  };
  const accent = accentByTone[tone];
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/95 px-8 dark:bg-arti-background/95 text-center">
      {tone === "loading" && (
        <LoadingSpinner
          size="md"
          className="border-emerald-500/30 border-t-emerald-400 dark:border-emerald-500/30 dark:border-t-emerald-400"
        />
      )}
      <div
        className={`font-mono text-[12px] font-bold uppercase tracking-widest ${accent}`}
      >
        {title}
      </div>
      <p className="max-w-sm font-mono text-[11px] leading-relaxed text-slate-500 dark:text-slate-500">
        {detail}
      </p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          disabled={action.disabled}
          className="mt-1 border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {action.label}
        </button>
      )}
      {secondaryAction && (
        <button
          type="button"
          onClick={secondaryAction.onClick}
          disabled={secondaryAction.disabled}
          className="border border-slate-400/40 bg-slate-500/10 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700 hover:bg-slate-500/20 dark:text-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {secondaryAction.label}
        </button>
      )}
    </div>
  );
}

function ViewportToggle({
  value,
  onChange,
}: {
  value: Viewport;
  onChange: (next: Viewport) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Preview viewport width"
      className="flex flex-shrink-0 border border-slate-300/70 dark:border-slate-700"
    >
      {(["desktop", "mobile"] as const).map((mode) => {
        const active = value === mode;
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(mode)}
            title={mode === "desktop" ? "Desktop width (full)" : "Mobile width"}
            className={`flex items-center justify-center px-1.5 py-1 transition-colors ${
              active
                ? "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            }`}
          >
            {mode === "desktop" ? <DesktopGlyph /> : <MobileGlyph />}
          </button>
        );
      })}
    </div>
  );
}

function DesktopGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden
    >
      <rect x="1.5" y="2.5" width="13" height="8.5" rx="1" />
      <path d="M6 13.5h4M8 11v2.5" strokeLinecap="round" />
    </svg>
  );
}

function MobileGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden
    >
      <rect x="4.5" y="1.5" width="7" height="13" rx="1.5" />
      <path d="M7 12.5h2" strokeLinecap="round" />
    </svg>
  );
}
